 /**
  * SEO HEADING STRUCTURE - 스터디 타이머 (Korean)
  */
 
 import { Header } from "@/components/Header";
 import { Footer } from "@/components/Footer";
 import { SEO } from "@/components/SEO";
 import { Button } from "@/components/ui/button";
 import { Card, CardContent } from "@/components/ui/card";
 import { Clock, Timer, BarChart3, Bell, ArrowRight, Zap, Target, Brain } from "lucide-react";
 import { Link } from "react-router-dom";
 
 const features = [
   {
     icon: Timer,
     title: "포모도로 타이머",
     description: "25분 집중, 5분 휴식의 과학적인 포모도로 기법으로 생산성을 극대화하세요.",
   },
   {
     icon: Clock,
     title: "커스텀 타이머",
     description: "25/5, 50/10, 90/20 등 자신에게 맞는 집중 시간을 설정하세요.",
   },
   {
     icon: BarChart3,
     title: "통계 및 분석",
     description: "일별, 주별 학습 시간을 추적하고 생산성 패턴을 분석하세요.",
   },
   {
     icon: Bell,
     title: "알림 기능",
     description: "세션 종료, 휴식 시작, 목표 달성 시 알림을 받으세요.",
   },
   {
     icon: Target,
     title: "목표 설정",
     description: "일일 학습 목표를 설정하고 진행 상황을 실시간으로 확인하세요.",
   },
   {
     icon: Brain,
     title: "집중 모드",
     description: "방해 요소를 차단하고 깊은 집중 상태를 유지하세요.",
   },
 ];
 
 export default function StudyTimerKr() {
   return (
     <div className="min-h-screen bg-background">
       <SEO 
         title="스터디 타이머 | CSW | Online Workspace | Productivity Tool | Study Stream"
         description="무료 포모도로 타이머로 집중력을 높이세요. 학습 시간 추적, 통계 분석, 목표 설정 기능을 제공합니다."
         keywords="스터디 타이머, 포모도로 타이머, 공부 타이머, 집중 타이머, 학습 타이머, 뽀모도로"
         canonicalUrl="/kr/study-timer/"
       />
       <Header />
       
       <main>
         {/* Hero Section */}
         <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
           <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
           <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
           
           <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
             <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in">
               <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                 <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                 <span className="text-sm font-medium text-muted-foreground">집중력을 높이세요</span>
               </div>
               
               <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                 <span className="text-gradient">스터디 타이머</span>
               </h1>
               
               <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl mx-auto">
                 포모도로 기법으로 집중력을 극대화하고 생산성을 높이세요.
               </p>
               
               <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                 <Button 
                   size="lg" 
                   className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                   asChild
                 >
                   <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                     타이머 시작하기
                     <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                   </a>
                 </Button>
               </div>
               
               <div className="flex items-center justify-center gap-8 pt-8">
                 <div>
                   <p className="text-3xl font-bold text-gradient">25/5</p>
                   <p className="text-sm text-muted-foreground">포모도로</p>
                 </div>
                 <div className="w-px h-12 bg-border" />
                 <div>
                   <p className="text-3xl font-bold text-gradient">50/10</p>
                   <p className="text-sm text-muted-foreground">긴 세션</p>
                 </div>
                 <div className="w-px h-12 bg-border" />
                 <div>
                   <p className="text-3xl font-bold text-gradient">무료</p>
                   <p className="text-sm text-muted-foreground">영원히</p>
                 </div>
               </div>
             </div>
           </div>
         </section>
 
         {/* Features Grid */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
           
           <div className="container mx-auto max-w-6xl relative z-10">
             <div className="text-center mb-16 animate-fade-in">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 <span className="text-gradient">타이머</span> 기능
               </h2>
               <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                 효과적인 학습을 위한 모든 타이머 기능
               </p>
             </div>
 
             <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
               {features.map((feature) => {
                 const Icon = feature.icon;
                 return (
                   <Card
                     key={feature.title}
                     className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl"
                   >
                     <CardContent className="p-8 space-y-4">
                       <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                         <Icon className="w-6 h-6 text-primary" />
                       </div>
                       
                       <h3 className="text-xl font-semibold text-foreground font-display">
                         {feature.title}
                       </h3>
                       
                       <p className="text-muted-foreground leading-relaxed">
                         {feature.description}
                       </p>
                     </CardContent>
                   </Card>
                 );
               })}
             </div>
           </div>
         </section>
 
         {/* CTA Section */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
           
           <div className="max-w-4xl mx-auto text-center relative z-10">
             <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
               지금 바로 <span className="text-gradient">집중</span>하세요
             </h2>
             <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
               무료 포모도로 타이머로 생산성을 높이세요.
             </p>
             <Button 
               size="lg" 
               className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
               asChild
             >
               <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                 무료로 시작하기
                 <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
               </a>
             </Button>
           </div>
         </section>
 
         {/* Related Pages */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
           
           <div className="container mx-auto max-w-6xl relative z-10">
             <div className="text-center mb-12">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 더 많은 <span className="text-gradient">기능</span> 살펴보기
               </h2>
             </div>
             
             <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
               {[
                 { name: "스터디 스트림", path: "/kr/study-stream/" },
                 { name: "온라인 작업 공간", path: "/kr/online-workspace/" },
                 { name: "포모도로 기법", path: "/kr/pomodoro-technique/" },
                 { name: "함께 공부하기", path: "/kr/study-with-me/" },
               ].map((link) => (
                 <Link
                   key={link.path}
                   to={link.path}
                   className="group p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-lg text-center"
                 >
                   <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                     {link.name}
                   </span>
                 </Link>
               ))}
             </div>
           </div>
         </section>
       </main>
       
       <Footer />
     </div>
   );
 }